import { Component, computed, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  placeholderMail,
  prefillMail,
  prefillValues,
  resolvePlaceholders,
  toFelder
} from '../document-fields/document-fields';
import { FilingRequest } from '../mail-upload/filing-pipeline.service';
import { MailUpload } from '../mail-upload/mail-upload';
import { MailService } from '../mail-upload/mail.service';
import { FilingSteps, FilingStepsState } from './filing-steps';

/**
 * The filing steps: dossier, folder, document fields, and the upload that ends
 * them in the confirmation.
 */
@Component({
  selector: 'cmi-filing-flow',
  imports: [FilingSteps, MailUpload],
  templateUrl: './filing-flow.html',
  // Fills the rest of the home page, so the action bar has a bottom to sit on.
  host: { class: 'mt-4 flex flex-1 flex-col' }
})
export class FilingFlow {
  private readonly _mailService = inject(MailService);
  private readonly _router = inject(Router);

  /**
   * The whole mask, which starts out with what the open mail fills in. The
   * prefill is read off the mail once the mask is in, so it describes the mail
   * open by then. A mail change on the way resets the flow, which drops this
   * load altogether.
   */
  protected readonly steps = new FilingStepsState(
    (config, prefill) => ({
      form: config,
      values: prefillValues(
        config,
        prefill,
        prefillMail(
          this._mailService.getCurrentMailSubject(),
          this._mailService.getCurrentMailMetadata()
        )
      ),
      locked: new Set()
    }),
    () => this._mailService.openMail().counterpart
  );

  /** Made from the mail once it is read, which is when the placeholders get its values. */
  protected readonly felder = computed((): FilingRequest['felder'] => {
    if (!this.steps.fieldConfig.hasValue()) {
      return [];
    }

    const { config } = this.steps.fieldConfig.value();
    const values = this.steps.fieldValues();

    return (metadata) =>
      toFelder(config, resolvePlaceholders(config, values, placeholderMail(metadata)));
  });

  public constructor() {
    // Pinned, the pane stays open across mails. The dossier and the folder
    // describe the mail that was open when they were picked, so the next mail
    // gets steps that say nothing about the last one. Home routes a mail change
    // back through the entry point too, but when that lands on this page again
    // the router keeps this instance, so the reset is on this side.
    effect(() => {
      this._mailService.openMail();
      this.steps.reset();
    });
  }

  protected onUploaded(): void {
    void this._router.navigate(['/filed'], { queryParams: { justFiled: true } });
  }
}
